import { useState, useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  MessageBar,
  MessageBarBody,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogTrigger,
  Field,
  Avatar,
  Divider,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  ArrowSync24Regular,
  Add24Regular,
  Search24Regular,
  PersonAdd24Regular,
  PersonDelete24Regular,
  Dismiss24Regular,
  Mail24Regular,
  People24Regular,
  MoreVertical24Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  GroupOrMailbox,
  MailboxKind,
  Employee,
  SPListItem,
} from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canSyncData, canManageRoles } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import {
  syncMailboxesAndGroups,
  fetchGroupMembers,
  addGroupMember,
  removeGroupMember,
  createDistributionList,
  deleteGroup,
  type GroupMember,
} from '@/graph/mailboxService';
import { getInitials, formatDateTime, timeAgo } from '@/utils/formatters';

const KIND_LABEL: Record<MailboxKind, string> = {
  DistributionList: 'Distribution List',
  SharedMailbox: 'Shared Mailbox',
  M365Group: 'Microsoft 365 Group',
  SecurityGroup: 'Security Group',
};

const KIND_COLOR: Record<MailboxKind, 'brand' | 'success' | 'warning' | 'informative'> = {
  DistributionList: 'brand',
  SharedMailbox: 'success',
  M365Group: 'warning',
  SecurityGroup: 'informative',
};

// Avatar component uses a slightly different palette — map there:
const KIND_AVATAR_COLOR: Record<MailboxKind, 'brand' | 'colorful' | 'neutral'> = {
  DistributionList: 'brand',
  SharedMailbox: 'colorful',
  M365Group: 'colorful',
  SecurityGroup: 'neutral',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // Apple-style large title at top + single action button
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  // Soft pill filters instead of tabs
  filters: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    '@media (max-width: 480px)': {
      flexDirection: 'column' as const,
      alignItems: 'stretch' as const,
    },
  },
  search: {
    minWidth: '220px',
    flex: '1 1 280px',
    '@media (max-width: 480px)': {
      minWidth: 'unset',
      flex: '1 1 auto',
    },
  },
  // Clean list — no dividers, just spacing
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    padding: '4px',
    boxShadow: tokens.shadow2,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 14px',
    borderRadius: tokens.borderRadiusLarge,
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  rowHover: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  rowMeta: {
    flex: 1,
    minWidth: 0,
  },
  rowTitle: {
    display: 'block',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  rowSub: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    color: tokens.colorNeutralForeground3,
  },
  memberItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  empty: {
    padding: '64px 24px',
    textAlign: 'center' as const,
    color: tokens.colorNeutralForeground3,
  },
});

export function MailboxesAndDLs() {
  const styles = useStyles();
  const { graphClient, currentUser, listIds } = useAppContext();

  if (currentUser && !canSyncData(currentUser.role)) {
    return <AccessDenied reason="Mailboxes & Distribution Lists are Admin-only." />;
  }

  const { items: entries, loading, refresh } = useSharePointList<GroupOrMailbox>(
    'OC_GroupsAndMailboxes',
    { autoFetch: !!listIds },
  );
  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });

  const [kindFilter, setKindFilter] = useState<'all' | MailboxKind>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SPListItem<GroupOrMailbox> | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<
    { type: 'success' | 'error'; text: string } | null
  >(null);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newMemberEmpId, setNewMemberEmpId] = useState('');
  const [newDl, setNewDl] = useState({ name: '', nickname: '', description: '' });

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: entries.length };
    for (const e of entries) {
      c[e.fields.Kind] = (c[e.fields.Kind] ?? 0) + 1;
    }
    return c;
  }, [entries]);

  const filtered = useMemo(() => {
    let res = entries;
    if (kindFilter !== 'all') res = res.filter((e) => e.fields.Kind === kindFilter);
    if (search) {
      const lower = search.toLowerCase();
      res = res.filter(
        (e) =>
          e.fields.Title?.toLowerCase().includes(lower) ||
          e.fields.Email?.toLowerCase().includes(lower),
      );
    }
    return res.sort((a, b) => (a.fields.Title ?? '').localeCompare(b.fields.Title ?? ''));
  }, [entries, kindFilter, search]);

  const lastSync = useMemo(() => {
    if (entries.length === 0) return null;
    return entries
      .map((e) => e.fields.LastSyncedAt)
      .filter(Boolean)
      .sort()
      .slice(-1)[0];
  }, [entries]);

  const handleSync = async () => {
    if (!graphClient) return;
    setSyncing(true);
    setMessage(null);
    try {
      const result = await syncMailboxesAndGroups(
        graphClient,
        currentUser?.email ?? '',
      );
      setMessage({
        type: 'success',
        text: `${result.groupsProcessed} groups, ${result.sharedMailboxesProcessed} shared mailboxes. ${result.created} new, ${result.updated} updated.`,
      });
      await refresh();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Sync failed',
      });
    } finally {
      setSyncing(false);
    }
  };

  const openDetail = useCallback(
    async (entry: SPListItem<GroupOrMailbox>) => {
      setSelected(entry);
      setMembers([]);
      if (!graphClient) return;
      if (entry.fields.Kind === 'SharedMailbox') return; // no members to fetch
      setLoadingMembers(true);
      try {
        const m = await fetchGroupMembers(graphClient, entry.fields.EntraObjectId);
        setMembers(m);
      } catch (err) {
        setMessage({
          type: 'error',
          text: err instanceof Error ? err.message : 'Failed to load members',
        });
      } finally {
        setLoadingMembers(false);
      }
    },
    [graphClient],
  );

  const handleAddMember = async () => {
    if (!graphClient || !selected || !newMemberEmpId) return;
    const emp = employees.find((e) => e.id === newMemberEmpId);
    if (!emp?.fields.EntraObjectId) {
      setMessage({ type: 'error', text: 'Employee has no Entra ID' });
      return;
    }
    setAddingMember(true);
    try {
      await addGroupMember(
        graphClient,
        selected.fields.EntraObjectId,
        emp.fields.EntraObjectId,
        currentUser?.email ?? '',
        emp.fields.Title,
        selected.fields.Title,
      );
      setNewMemberEmpId('');
      await openDetail(selected); // refresh
      setMessage({ type: 'success', text: `Added ${emp.fields.Title}` });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to add',
      });
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    if (!graphClient || !selected) return;
    try {
      await removeGroupMember(
        graphClient,
        selected.fields.EntraObjectId,
        member.id,
        currentUser?.email ?? '',
        member.displayName,
        selected.fields.Title,
      );
      await openDetail(selected);
      setMessage({ type: 'success', text: `Removed ${member.displayName}` });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove',
      });
    }
  };

  const handleCreateDl = async () => {
    if (!graphClient || !newDl.name || !newDl.nickname) return;
    try {
      await createDistributionList(
        graphClient,
        newDl.name,
        newDl.nickname,
        newDl.description,
        currentUser?.email ?? '',
      );
      setShowNewDialog(false);
      setNewDl({ name: '', nickname: '', description: '' });
      setMessage({
        type: 'success',
        text: `Created "${newDl.name}" — run Sync to pull it in`,
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Create failed',
      });
    }
  };

  const handleDelete = async (entry: SPListItem<GroupOrMailbox>) => {
    if (!graphClient) return;
    if (!confirm(`Delete "${entry.fields.Title}"? This cannot be undone.`)) return;
    try {
      await deleteGroup(
        graphClient,
        entry.fields.EntraObjectId,
        entry.fields.Title,
        currentUser?.email ?? '',
      );
      setSelected(null);
      await refresh();
      setMessage({ type: 'success', text: 'Deleted' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Delete failed',
      });
    }
  };

  const canEdit = currentUser ? canManageRoles(currentUser.role) : false;

  return (
    <div className={styles.page}>
      <div className={styles.titleRow}>
        <div>
          <PageTitle>Mailboxes & Groups</PageTitle>
          <Caption1>
            {entries.length} items · last sync{' '}
            {lastSync ? timeAgo(lastSync) : 'never'}
          </Caption1>
        </div>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <Button appearance="primary" icon={<MoreVertical24Regular />}>
              Actions
            </Button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={syncing ? <Spinner size="tiny" /> : <ArrowSync24Regular />}
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? 'Syncing…' : 'Sync from Microsoft'}
              </MenuItem>
              {canEdit && (
                <MenuItem
                  icon={<Add24Regular />}
                  onClick={() => setShowNewDialog(true)}
                >
                  New distribution list
                </MenuItem>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>

      {message && (
        <MessageBar intent={message.type}>
          <MessageBarBody>{message.text}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.filters}>
        <Input
          className={styles.search}
          contentBefore={<Search24Regular />}
          placeholder="Search mailboxes and groups"
          value={search}
          onChange={(_, d) => setSearch(d.value)}
        />
        <Dropdown
          placeholder="Type"
          value={kindFilter === 'all' ? `All (${counts.all ?? 0})` : `${KIND_LABEL[kindFilter]} (${counts[kindFilter] ?? 0})`}
          onOptionSelect={(_, d) =>
            setKindFilter(d.optionValue as 'all' | MailboxKind)
          }
          style={{ minWidth: 220 }}
        >
          <Option value="all" text={`All (${counts.all ?? 0})`}>
            All ({counts.all ?? 0})
          </Option>
          <Option
            value="DistributionList"
            text={`Distribution Lists (${counts.DistributionList ?? 0})`}
          >
            Distribution Lists ({counts.DistributionList ?? 0})
          </Option>
          <Option
            value="SharedMailbox"
            text={`Shared Mailboxes (${counts.SharedMailbox ?? 0})`}
          >
            Shared Mailboxes ({counts.SharedMailbox ?? 0})
          </Option>
          <Option
            value="M365Group"
            text={`Microsoft 365 Groups (${counts.M365Group ?? 0})`}
          >
            Microsoft 365 Groups ({counts.M365Group ?? 0})
          </Option>
          <Option
            value="SecurityGroup"
            text={`Security Groups (${counts.SecurityGroup ?? 0})`}
          >
            Security Groups ({counts.SecurityGroup ?? 0})
          </Option>
        </Dropdown>
      </div>

      {loading ? (
        <div className={styles.empty}>
          <Spinner label="Loading…" />
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.empty}>
          <Mail24Regular style={{ fontSize: 32, marginBottom: 8 }} />
          <Body1>No mailboxes or groups yet.</Body1>
          <Caption1>Click <strong>Actions → Sync from Microsoft</strong> to pull them in.</Caption1>
        </div>
      ) : (
        <div className={styles.list}>
          {filtered.map((e) => (
            <div
              key={e.id}
              className={`${styles.row} ${styles.rowHover}`}
              onClick={() => openDetail(e)}
            >
              <Avatar
                icon={<Mail24Regular />}
                color={KIND_AVATAR_COLOR[e.fields.Kind]}
                size={40}
                shape="square"
              />
              <div className={styles.rowMeta}>
                <Body1 className={styles.rowTitle}>{e.fields.Title}</Body1>
                <Caption1 className={styles.rowSub}>
                  {e.fields.Email || 'no email'}
                </Caption1>
              </div>
              <Badge
                appearance="tint"
                color={KIND_COLOR[e.fields.Kind]}
                size="small"
              >
                {KIND_LABEL[e.fields.Kind]}
              </Badge>
              <Caption1 style={{ minWidth: 60, textAlign: 'right' as const }}>
                {e.fields.MemberCount ?? 0} members
              </Caption1>
            </div>
          ))}
        </div>
      )}

      {/* Detail drawer */}
      <Drawer
        type="overlay"
        position="end"
        open={!!selected}
        onOpenChange={() => setSelected(null)}
        size="medium"
      >
        {selected && (
          <>
            <DrawerHeader>
              <DrawerHeaderTitle
                action={
                  <Menu>
                    <MenuTrigger disableButtonEnhancement>
                      <Button
                        appearance="subtle"
                        icon={<MoreVertical24Regular />}
                      />
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItem
                          icon={<Dismiss24Regular />}
                          onClick={() => setSelected(null)}
                        >
                          Close
                        </MenuItem>
                        {canEdit && (
                          <MenuItem
                            icon={<Delete24Regular />}
                            onClick={() => handleDelete(selected)}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                }
              >
                {selected.fields.Title}
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
              <div style={{ marginBottom: 20 }}>
                <Badge
                  appearance="filled"
                  color={KIND_COLOR[selected.fields.Kind]}
                  size="small"
                >
                  {KIND_LABEL[selected.fields.Kind]}
                </Badge>
                <Caption1 style={{ display: 'block', marginTop: 8 }}>
                  {selected.fields.Email || 'no email'}
                </Caption1>
                {selected.fields.Description && (
                  <Body2 style={{ marginTop: 8 }}>
                    {selected.fields.Description}
                  </Body2>
                )}
                <Caption1 style={{ display: 'block', marginTop: 4 }}>
                  Last synced {formatDateTime(selected.fields.LastSyncedAt)}
                </Caption1>
              </div>

              {selected.fields.Kind !== 'SharedMailbox' && (
                <>
                  <Divider />
                  <Body1
                    style={{
                      fontWeight: 600,
                      margin: '16px 0 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <People24Regular />
                    Members ({members.length})
                  </Body1>
                  {loadingMembers ? (
                    <Spinner size="small" />
                  ) : (
                    <>
                      {members.map((m) => (
                        <div key={m.id} className={styles.memberItem}>
                          <Avatar
                            name={m.displayName}
                            initials={getInitials(m.displayName)}
                            size={32}
                            color="brand"
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Body2 style={{ fontWeight: 500 }}>
                              {m.displayName}
                            </Body2>
                            <Caption1>
                              {m.mail ?? m.userPrincipalName}
                            </Caption1>
                          </div>
                          {canEdit && (
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<PersonDelete24Regular />}
                              onClick={() => handleRemoveMember(m)}
                            />
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {canEdit && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        backgroundColor: tokens.colorNeutralBackground2,
                        borderRadius: tokens.borderRadiusMedium,
                      }}
                    >
                      <Field label="Add a member">
                        <Dropdown
                          placeholder="Pick an employee"
                          value={
                            employees.find((e) => e.id === newMemberEmpId)
                              ?.fields.Title ?? ''
                          }
                          onOptionSelect={(_, d) =>
                            setNewMemberEmpId(d.optionValue as string)
                          }
                        >
                          {employees
                            .filter(
                              (e) =>
                                e.fields.EntraObjectId &&
                                e.fields.Status === 'Active' &&
                                !members.some(
                                  (m) => m.id === e.fields.EntraObjectId,
                                ),
                            )
                            .slice(0, 100)
                            .map((e) => (
                              <Option key={e.id} value={e.id}>
                                {e.fields.Title}
                              </Option>
                            ))}
                        </Dropdown>
                      </Field>
                      <Button
                        appearance="primary"
                        icon={
                          addingMember ? (
                            <Spinner size="tiny" />
                          ) : (
                            <PersonAdd24Regular />
                          )
                        }
                        disabled={!newMemberEmpId || addingMember}
                        onClick={handleAddMember}
                        style={{ marginTop: 8 }}
                      >
                        {addingMember ? 'Adding…' : 'Add member'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </DrawerBody>
          </>
        )}
      </Drawer>

      {/* Create new DL dialog */}
      <Dialog
        open={showNewDialog}
        onOpenChange={() => setShowNewDialog(false)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New distribution list</DialogTitle>
            <DialogContent>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column' as const,
                  gap: 12,
                }}
              >
                <Field label="Display name" required>
                  <Input
                    value={newDl.name}
                    onChange={(_, d) =>
                      setNewDl((v) => ({ ...v, name: d.value }))
                    }
                    placeholder="e.g. Engineering Team"
                  />
                </Field>
                <Field label="Mail nickname" required hint="Used in the email address: <nickname>@2sdtechnologies.com">
                  <Input
                    value={newDl.nickname}
                    onChange={(_, d) =>
                      setNewDl((v) => ({ ...v, nickname: d.value.replace(/[^a-z0-9_-]/gi, '') }))
                    }
                    placeholder="engineering"
                  />
                </Field>
                <Field label="Description">
                  <Input
                    value={newDl.description}
                    onChange={(_, d) =>
                      setNewDl((v) => ({ ...v, description: d.value }))
                    }
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={handleCreateDl}
                disabled={!newDl.name || !newDl.nickname}
              >
                Create
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
